import * as React from 'react';
import SectionHeader from './SectionHeader';
import { statisticsForOverviewSection } from '../../helpers';
import {
    IntegrationCapability,
    IntegrationType,
    OverviewCodeContributor,
    OverviewSection,
    Statistic,
    StatisticType,
    StatisticWithAssociatedInfo,
} from '../../types';
import { useSelector } from 'react-redux';
import {
    selectAllIntegrationsForCapabilities,
    selectIsReleaseKickedOff,
    selectReleaseOverviewStats,
    selectVCSIntegration,
    selectReleaseVersion,
} from '../../selectors/selectors';
import { RootState } from '../../reducers/reducers';
import Avatar from 'antd/lib/avatar';
import Col from 'antd/lib/grid/col';
import Divider from 'antd/lib/divider';
import Row from 'antd/lib/grid/row';
import Space from 'antd/lib/space';
import Tooltip from 'antd/lib/tooltip';
import { ReleaseOverviewStatCard } from '../StatCard';
import FilesChanged from './FilesChanged';
import ContributorChart from './ContributorChart';
import { antGrey, getAvatarColorForName } from '../styles/globalStyles';
import { displayUserNameLong } from '../../helpers/displayHelpers';
import Text from 'antd/lib/typography/Text';
import { GRID_GAP, GRID_CARD_MIN_WIDTH, GRID_MIN_WIDTH } from '../../constants';
import { FeatureSection } from '../../typescriptHelpers';
import { EmptyStateWrapper } from '../releaseEmptyState/EmptyStateWrapper';

interface CodeSectionProps {
    appId: string;
    releaseId: string;
    isLoadingReleaseStatsHistory: boolean;
    releaseStatsByTypes: Partial<Record<StatisticType, StatisticWithAssociatedInfo[]>>;
    currentReleaseStats: Statistic[];
}

const CodeSection: React.FC<CodeSectionProps> = (props: CodeSectionProps) => {
    const { appId, releaseId, isLoadingReleaseStatsHistory, releaseStatsByTypes, currentReleaseStats } = props;
    const currentReleaseVersion = useSelector((state: RootState) => selectReleaseVersion(state, releaseId));

    const integrations = useSelector((state: RootState) =>
        selectAllIntegrationsForCapabilities(state, appId, [
            IntegrationCapability.VersionControl,
            IntegrationCapability.BuildSystem,
        ]),
    );
    const vcsIntegration = useSelector((state: RootState) => selectVCSIntegration(state, props.appId));

    const isKickedOff = useSelector((state: RootState) => selectIsReleaseKickedOff(state, releaseId));
    const overview = useSelector((state: RootState) => selectReleaseOverviewStats(state, releaseId));

    const codeStats =
        currentReleaseStats?.filter((stat) => {
            return statisticsForOverviewSection(OverviewSection.Code, vcsIntegration?.id).includes(stat.type);
        }) ?? [];

    if (!codeStats || codeStats.length === 0) {
        return null;
    }

    const topChangedFiles = overview.topChangedFiles;
    const totalFilesChanged = overview.totalFilesChanged;
    const codeContributors = overview.codeContributors;

    const members = codeContributors?.map((member) => {
        let name;
        if (member.firstName) {
            name = member.firstName;
        } else if (member.email) {
            name = member.email;
        } else {
            name = member.id;
        }
        return {
            name,
            itemOfWorkCount: member.itemOfWorkCount,
        };
    });
    const emptyChange = <Text style={{ color: antGrey.grey7, fontSize: '18px' }}>--</Text>;

    return (
        <div>
            <SectionHeader
                header={
                    <Space>
                        <span>Code</span>
                        <EmptyStateWrapper
                            placement="right"
                            appId={appId}
                            shouldHideDotOnHover={false}
                            featureSections={[FeatureSection.OverviewCode]}
                            releaseId={releaseId}
                            shouldShowAsTooltip
                            customWrapperStyle={{ height: 20 }}
                        />
                    </Space>
                }
                relatedIntegrations={integrations}
            />
            <Row>
                <Col span={20}>
                    <div
                        style={{
                            display: 'grid',
                            gap: GRID_GAP,
                            gridTemplateColumns: `repeat(auto-fill, minmax(${GRID_CARD_MIN_WIDTH}px, 1fr))`,
                            minWidth: GRID_MIN_WIDTH,
                            alignItems: 'start',
                        }}
                    >
                        {codeContributors?.length > 0 && (
                            <div style={{ gridColumn: 'span 2' }}>
                                <div
                                    style={{
                                        display: 'flex',
                                        minHeight: '175px',
                                        borderRadius: '8px',
                                        backgroundColor: antGrey.grey2,
                                        color: antGrey.grey7,
                                        padding: '18px',
                                    }}
                                >
                                    <div style={{ flex: 3 }}>
                                        <ContributorChart title={'Contributors'} members={members} />
                                    </div>
                                    <Divider
                                        dashed
                                        type="vertical"
                                        style={{ marginRight: 20, marginLeft: 20, height: '10em' }}
                                    />
                                    <div style={{ flex: 1 }}>
                                        <AvatarGrid contributors={codeContributors} />
                                    </div>
                                </div>
                            </div>
                        )}
                        {codeStats.map((stat) => {
                            const sampleStats = releaseStatsByTypes[stat.type];

                            return (
                                <ReleaseOverviewStatCard
                                    key={stat.type}
                                    type={stat.type}
                                    change={isKickedOff ? stat.change : emptyChange}
                                    changeStatus={isKickedOff ? stat.changeStatus : null}
                                    value={stat.value}
                                    unit={stat.unit}
                                    isLoadingStats={isLoadingReleaseStatsHistory}
                                    highlightedVersion={currentReleaseVersion}
                                    sampleStats={sampleStats}
                                    average={stat.average}
                                />
                            );
                        })}
                    </div>
                    {totalFilesChanged !== null && vcsIntegration?.id !== IntegrationType.GitLabVCS && (
                        <div
                            style={{
                                marginTop: GRID_GAP,
                                minWidth: GRID_MIN_WIDTH,
                                maxWidth: GRID_CARD_MIN_WIDTH * 3 + GRID_GAP * 2,
                            }}
                        >
                            <FilesChanged
                                topChangedFiles={topChangedFiles ? topChangedFiles : []}
                                totalFilesChanged={totalFilesChanged}
                            />
                        </div>
                    )}
                </Col>
            </Row>
        </div>
    );
};

const AvatarGrid = ({ contributors }: { contributors: OverviewCodeContributor[] }) => {
    const contributorNameStringForContributor = (contributor: OverviewCodeContributor) => {
        let nameString = contributor.id;

        if (contributor.firstName) {
            nameString = contributor.firstName;
        } else if (contributor.lastName) {
            nameString = contributor.lastName;
        } else if (contributor.email) {
            nameString = contributor.email;
        }

        return nameString;
    };

    return (
        <Avatar.Group
            maxCount={11}
            maxPopoverTrigger="click"
            style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}
            maxStyle={{
                color: antGrey.grey8,
                backgroundColor: antGrey.grey4,
                cursor: 'pointer',
                marginLeft: 0,
                width: '36px',
                height: '36px',
                lineHeight: '36px',
            }}
            size={36}
            className="release-overview-avatar-group"
        >
            {contributors.map((contributor) => {
                const contributorNameString = contributorNameStringForContributor(contributor);

                return (
                    <Tooltip key={contributor.id} title={displayUserNameLong(contributor)} placement="top">
                        <Avatar
                            style={{
                                fontSize: 12,
                                backgroundColor: getAvatarColorForName(contributorNameString),
                                flexShrink: 0,
                            }}
                            size={36}
                            src={contributor.avatar}
                        >
                            {contributor.id.charAt(0).toUpperCase()}
                        </Avatar>
                    </Tooltip>
                );
            })}
        </Avatar.Group>
    );
};
export default CodeSection;
